import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { DesignContext } from 'contexts/DesignContext'

import { ConfigContext } from 'contexts/ConfigContext'
import objectPath from 'object-path'
import { Form, Col, Row, InputGroup } from 'react-bootstrap'
import Label from './Label'
import InputPrepend from './InputPrepend'
import InputAppend from './InputAppend'
import {
  cancelDebounceCallback,
  debounceCallback,
} from 'utility/debounceCallback.util'
import { setApi, getServiceRequest, getExternalRequest } from '../action/Api'
import { createLabel } from '../action/Option'
import { getCurrentUi } from '../action/Data'
import useAuth from 'hooks/useAuth'
import NumberFormat from 'react-number-format'
import { createUischemaStyle } from './Style'
import { handleEnterAction } from '../action/Action'

const CustomInputSearch = (props) => {
  let {
    uischema,
    updateData,
    copyData = () => {},
    errors = {},
    value,
    label,
    setIsOnChange,
    fieldName,
    fieldKey,
    config = {},
  } = props
  let {
    format,
    name,
    key,
    elementWidth,
    placeholder,
    minLength,
    maxLength,
    readonly,
    controlId,
    suffixText,
    require,
    optionType = '',
    searchInputType = 'text',
    isSearchText,
    isSearchNumber,
    triggerSearch = 'onChange',
  } = uischema

  const designContext = useContext(DesignContext)
  const {
    elementAction,
    jsonForm,
    getCurrentUi: getCurrentUiFromContext,
    updateUiSchema,
    setFieldFormData = () => {},
    isUiChanged = {},
    setIsUiChanged = () => {},
    isResetData,
  } = designContext
  const configContext = useContext(ConfigContext)
  const { layoutType } = configContext.state
  const { user } = useAuth()

  const [keyword, setKeyword] = useState(value ?? '')
  const [options, setOptions] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [isFocus, setIsFocus] = useState(false)
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1)
  const isResetField = objectPath.get(isUiChanged, ['isResetField', key])
  const requestRef = useRef(0)
  const optionRefs = useRef([])
  const isSelectingOptionRef = useRef(false)

  let errorArray = objectPath.get(errors, key) || []
  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', key])
  let isEnable = objectPath.get(elementAction, ['isEnable', key])
  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return readonly
  }, [isEnable, readonly])

  let hasRequire = objectPath.has(elementAction, ['isRequire', key])
  let requireMessage = objectPath.get(elementAction, ['isRequire', key])
  let showRequireMessage = hasRequire
    ? typeof requireMessage !== 'boolean'
    : require
  let isRequire = hasRequire || require
  let showRequire = value ? false : isRequire ? true : false

  let hasWarning = objectPath.has(elementAction, ['isWarning', key])
  let warningMessage = objectPath.get(elementAction, ['isWarning', key])
  let showWarningMessage = hasWarning
    ? typeof warningMessage !== 'boolean'
    : warningMessage

  let actionUpdate = objectPath.get(elementAction, ['actionUpdate'])
  let updateOption = objectPath.get(elementAction, ['updateOption'])
  const optionData = objectPath.get(uischema, `${optionType}`) || {}
  const {
    optionLabel = 'label',
    optionValue = 'value',
    titleValue = [],
    limit,
    labelColor,
  } = optionData

  const uiStyle = useMemo(
    () => createUischemaStyle({ uischema, showRequire }),
    [uischema, showRequire],
  )

  const debounceName = useMemo(() => `input-search-${key}`, [key])
  const searchTrigger = ['onChange', 'onEnter', 'onBlur'].includes(
    triggerSearch,
  )
    ? triggerSearch
    : 'onChange'
  const isDelaySearch = searchTrigger !== 'onChange'

  const updateInputData = (value, isOnChange = true) => {
    updateData(value, isOnChange, { skipSearch: isDelaySearch })
  }

  const triggerSearchData = (value, eventName) => {
    if (isSelectingOptionRef.current) return
    if (searchTrigger !== eventName) return
    if (eventName === 'onEnter' && !checkNumberSearchValue(value)) return
    updateData(value, false)
  }

  const getCurrencyValue = (value) => {
    let stringValue = `${value ?? ''}`
    let replaceString = stringValue.replace(',', '')
    return Number(replaceString)
  }

  const checkNumberSearchValue = (value) => {
    let stringValue = `${value ?? ''}`.trim()
    return /^\d+$/.test(stringValue)
  }

  const updateSelectOption = () => {
    if (actionUpdate) {
      actionUpdate.forEach(async (selectKey) => {
        let isUpdateOption = objectPath.get(updateOption, selectKey)
        if (!isUpdateOption) return
        let selectUi = getCurrentUiFromContext(selectKey)
        let { optionType } = selectUi
        let optionData = objectPath.get(selectUi, `${optionType}`)
        let { service, endpoint, pathParams, queryParams, url } = optionData

        let api = setApi(
          {
            apiType: optionType,
            api: url,
            service: service,
            endpoint: endpoint,
            pathParams: pathParams,
            queryParams: queryParams,
            jsonForm,
          },
          jsonForm.formData,
        )
        if (optionType === 'internal') {
          if (!service || !endpoint) return
          optionData = await getServiceRequest(api, optionData, selectUi)
        } else if (optionType === 'external') {
          if (!url) return
          optionData = await getExternalRequest(api, optionData, selectUi)
        }
        updateUiSchema(selectUi.key, optionType, optionData)
      })
    }
  }

  useEffect(() => {
    debounceCallback(updateSelectOption, [])
  }, [value])

  const colorByTheme = useMemo(
    () => (layoutType === 'dark' ? '#adb7be' : '#495057'),
    [],
  )

  useEffect(() => {
    if (format === 'user') {
      let value = objectPath.get(user, 'userId')
      updateData(value, false)
    }
  }, [format])

  useEffect(() => {
    setKeyword(format === 'user' ? user.name : value ?? '')
  }, [value, format, user.name])

  useEffect(() => {
    if (!isResetData && !isResetField) return
    setKeyword(format === 'user' ? user.name : value ?? '')
    setOptions([])
    setShowDropdown(false)
    setActiveOptionIndex(-1)
    if (isResetField) {
      objectPath.del(isUiChanged, ['isResetField', key])
      setIsUiChanged(isUiChanged)
    }
  }, [isResetData, isResetField, value, format, user.name])

  const displayInputValue = useMemo(() => {
    if (format === 'user') return user.name
    if (format === 'currency') return isDelaySearch ? keyword : value ?? ''
    return isDelaySearch ? keyword : value ?? ''
  }, [format, isDelaySearch, keyword, user.name, value])

  const isShowClearButton =
    !isReadOnly &&
    format !== 'user' &&
    displayInputValue !== null &&
    displayInputValue !== undefined &&
    `${displayInputValue}` !== ''

  const isEnableSearchText = objectPath.has(uischema, 'isSearchText')
    ? isSearchText
    : searchInputType === 'text'
  const isEnableSearchNumber = objectPath.has(uischema, 'isSearchNumber')
    ? isSearchNumber
    : searchInputType === 'number'

  const checkSearchInputType = (value) => {
    let stringValue = `${value ?? ''}`.trim()
    if (!stringValue) return false
    let isNumberValue = /^\d+$/.test(stringValue)
    let isTextValue = /[^\d\s]/.test(stringValue)
    return (
      (isEnableSearchText && isTextValue) ||
      (isEnableSearchNumber && isNumberValue)
    )
  }

  const getRequestFormData = (value) => {
    let formData = { ...(jsonForm.formData || {}) }
    objectPath.set(formData, key, value)
    if (name) {
      objectPath.set(formData, name, value)
    }
    return formData
  }

  const getSearchApi = (requestFormData) => {
    let {
      service,
      endpoint,
      pathParams,
      queryParams,
      url,
      api,
      page,
      perPage,
    } = optionData

    if (optionType === 'internal' && (!service || !endpoint)) return
    if (optionType === 'external' && !url) return

    let requestApi = api || url
    if (optionType === 'external' && !api && queryParams?.length > 0) {
      requestApi = url?.split('?')?.shift() || url
    }

    return setApi(
      {
        apiType: optionType,
        api: requestApi,
        service: service,
        endpoint: endpoint,
        pathParams: pathParams,
        queryParams: queryParams,
        isOption: true,
        page,
        perPage,
        jsonForm,
      },
      requestFormData,
      { searchData: requestFormData, keyword: requestFormData },
    )
  }

  const createDropdownOptions = (options = []) => {
    if (!Array.isArray(options)) {
      options = options ? [options] : []
    }
    let labelOptions = createLabel(titleValue, options)
    if (labelOptions.length > 0) {
      options = labelOptions
    }
    if (limit) {
      options = options.slice(0, Number(limit))
    }
    return options
  }

  const getSearchOptions = async (searchValue) => {
    let currentRequest = requestRef.current + 1
    requestRef.current = currentRequest
    if (
      !isFocus ||
      isReadOnly ||
      !optionType ||
      !checkSearchInputType(searchValue)
    ) {
      setOptions([])
      setShowDropdown(false)
      return
    }

    let requestFormData = getRequestFormData(searchValue)
    let api = getSearchApi(requestFormData)
    if (!api) {
      setOptions([])
      setShowDropdown(false)
      return
    }

    let nextOptionData = { dataKey: 'data', ...optionData }
    if (optionType === 'internal') {
      nextOptionData = await getServiceRequest(
        api,
        nextOptionData,
        uischema,
        requestFormData,
      )
    } else if (optionType === 'external') {
      nextOptionData = await getExternalRequest(api, nextOptionData, uischema)
    }
    if (requestRef.current !== currentRequest) return

    let responseOptions = createDropdownOptions(nextOptionData?.options || [])
    setOptions(responseOptions)
    setShowDropdown(responseOptions.length > 0 && isFocus)
  }

  useEffect(() => {
    if (showDropdown && options.length > 0) {
      setActiveOptionIndex(0)
    } else {
      setActiveOptionIndex(-1)
    }
  }, [showDropdown, options])

  useEffect(() => {
    if (!showDropdown || activeOptionIndex < 0) return
    let activeOption = optionRefs.current[activeOptionIndex]
    if (activeOption) {
      activeOption.scrollIntoView({ block: 'nearest' })
    }
  }, [activeOptionIndex, showDropdown])

  useEffect(() => {
    debounceCallback(getSearchOptions, [keyword], debounceName)
    return () => cancelDebounceCallback(debounceName)
  }, [
    keyword,
    optionType,
    searchInputType,
    isSearchText,
    isSearchNumber,
    isFocus,
    isReadOnly,
  ])

  const displayValue = (value) => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'object') return JSON.stringify(value)
    return value
  }

  const getOptionLabel = (option) => {
    if (typeof option !== 'object' || option === null) return displayValue(option)
    let label = objectPath.get(option, '@label')
    if (label !== undefined && label !== null) return displayValue(label)
    if (optionLabel && objectPath.has(option, optionLabel)) {
      return displayValue(objectPath.get(option, optionLabel))
    }
    if (optionValue && objectPath.has(option, optionValue)) {
      return displayValue(objectPath.get(option, optionValue))
    }
    return displayValue(option)
  }

  const getOptionValue = (option) => {
    if (typeof option !== 'object' || option === null) return option
    if (optionValue && objectPath.has(option, optionValue)) {
      return objectPath.get(option, optionValue)
    }
    return objectPath.get(option, 'value') || option
  }

  const updateOtherParam = (selectValue = {}) => {
    let otherValues = objectPath.get(optionData, 'otherValues') || {}
    let otherParamUpdated = objectPath.get(isUiChanged, 'otherParamUpdated') || {}
    let { isDynamic = false, dynamicName = '', dynamicIndex = 0 } = config
    Object.keys(otherValues).forEach((paramKey) => {
      let { field } = objectPath.get(otherValues, paramKey) || {}
      if (!field) return
      let value = objectPath.get(selectValue, paramKey)
      let ui = getCurrentUi(field, jsonForm.schema, jsonForm.uischema) || {}
      if (ui.key !== field) return
      if (isDynamic) {
        let dynamicData = objectPath.get(jsonForm.formData, dynamicName) || []
        objectPath.set(dynamicData, `${dynamicIndex}.${ui.name}`, value)
        setFieldFormData(`${dynamicName}`, dynamicData)
        objectPath.set(otherParamUpdated, `${dynamicName}`, dynamicData)
      } else {
        setFieldFormData(ui.name, value)
        setFieldFormData(field, value)
        objectPath.set(otherParamUpdated, `${field}`, value)
      }
    })
    objectPath.set(isUiChanged, 'otherParamUpdated', otherParamUpdated)
    setIsUiChanged(isUiChanged)
  }

  const selectOption = (option) => {
    let value = getOptionValue(option)
    isSelectingOptionRef.current = true
    updateData(value, true)
    copyData(value)
    setIsOnChange(true)
    setKeyword(displayValue(value))
    updateOtherParam(option)
    setShowDropdown(false)
    setActiveOptionIndex(-1)
    setTimeout(() => {
      isSelectingOptionRef.current = false
    }, 200)
  }

  const clearInput = (event) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    updateData('', true)
    copyData('')
    setIsOnChange(true)
    setKeyword('')
    setOptions([])
    setShowDropdown(false)
    setActiveOptionIndex(-1)
  }

  const handleDropdownKeyDown = (ev) => {
    if (ev.key === 'ArrowDown') {
      if (options.length > 0) {
        ev.preventDefault()
        ev.stopPropagation()
        setShowDropdown(true)
        setActiveOptionIndex((currentIndex) => {
          if (!showDropdown || currentIndex < 0) return 0
          if (currentIndex >= options.length - 1) return currentIndex
          return currentIndex + 1
        })
        return true
      }
    }

    if (ev.key === 'ArrowUp') {
      if (options.length > 0) {
        ev.preventDefault()
        ev.stopPropagation()
        setShowDropdown(true)
        setActiveOptionIndex((currentIndex) => {
          if (!showDropdown || currentIndex < 0) return 0
          if (currentIndex <= 0) return currentIndex
          return currentIndex - 1
        })
        return true
      }
    }

    if (ev.key === 'Enter' && showDropdown && options.length > 0) {
      let optionIndex = activeOptionIndex >= 0 ? activeOptionIndex : 0
      let option = options[optionIndex]
      if (option) {
        ev.preventDefault()
        ev.stopPropagation()
        selectOption(option)
        return true
      }
    }

    if (ev.key === 'Escape' && showDropdown) {
      ev.preventDefault()
      ev.stopPropagation()
      setShowDropdown(false)
      setActiveOptionIndex(-1)
      return true
    }

    return false
  }

  const renderClearButton = () => {
    if (!isShowClearButton) return null
    return (
      <InputGroup.Append>
        <button
          type="button"
          className="btn btn-outline-secondary"
          style={{ padding: '0 10px', lineHeight: 1 }}
          aria-label="clear input"
          onMouseDown={(event) => event.preventDefault()}
          onClick={clearInput}
        >
          <i className="fas fa-times" />
        </button>
      </InputGroup.Append>
    )
  }

  const renderOptionLabel = (option, label, value, fontColor) => {
    let labelList = objectPath.get(option, '@labelList') || []
    if (Array.isArray(labelList) && labelList.length > 0) {
      return labelList.map(
        ({ label, fontColor: itemFontColor, fontWeight }, labelIndex) => {
          if (!label) return null
          return (
            <span
              key={`input-search-option-label-${key}-${labelIndex + 1}`}
              style={{
                color: itemFontColor || fontColor || colorByTheme,
                fontWeight: fontWeight || 'normal',
              }}
            >
              {label}
            </span>
          )
        },
      )
    }
    return label && label !== value ? <span>{label}</span> : null
  }

  const renderDropdown = () => {
    if (!showDropdown || options.length === 0) return null
    return (
      <div
        id={`input-search-list-${key}`}
        role="listbox"
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          minWidth: '100%',
          width: 'max-content',
          maxWidth: 'calc(100vw - 32px)',
          background: '#fff',
          border: '1px solid #dee2e6',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
          maxHeight: '250px',
          overflowY: 'auto',
          overflowX: 'auto',
          zIndex: 999,
        }}
      >
        {options.map((option, index) => {
          let value = displayValue(getOptionValue(option))
          let label = getOptionLabel(option)
          let fontColor = objectPath.get(option, `@fontColor`)
          let fontWeight = objectPath.get(option, `@fontWeight`)
          let labelList = objectPath.get(option, '@labelList') || []
          let hasLabelList = Array.isArray(labelList) && labelList.length > 0
          let isActive = activeOptionIndex === index
          if (labelColor) {
            fontColor = objectPath.get(option, labelColor) || fontColor
          }
          return (
            <div
              ref={(element) => {
                optionRefs.current[index] = element
              }}
              id={`input-search-option-${key}-${index + 1}`}
              key={`input-search-option-${key}-${index + 1}`}
              className="px-3 py-2 border-bottom"
              role="option"
              aria-selected={isActive}
              tabIndex={-1}
              style={{
                cursor: 'pointer',
                color: fontColor || colorByTheme,
                fontWeight: hasLabelList ? undefined : fontWeight,
                whiteSpace: 'nowrap',
                backgroundColor: isActive ? '#90caf9' : '#fff',
              }}
              onMouseEnter={() => setActiveOptionIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault()
                selectOption(option)
              }}
            >
              {value && !hasLabelList && (
                <strong className="mr-2">{value}</strong>
              )}
              {renderOptionLabel(option, label, value, fontColor)}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Form.Group className="mb-0" controlId={controlId}>
      <Row className="w-100 mx-0">
        <Label
          {...props}
          uischema={uischema}
          label={label}
          isRequire={isRequire}
          isEnable={!isReadOnly}
        />
        <Col sm={elementWidth} className="mt-1 p-0">
          <div style={{ position: 'relative' }}>
            <InputGroup>
              <InputPrepend
                {...props}
                isReadOnly={isReadOnly}
                uischema={uischema}
              />
              {format === 'currency' ? (
                <NumberFormat
                  className="form-control"
                  style={uiStyle}
                  name="value"
                  thousandSeparator={format === 'currency'}
                  autoComplete="off"
                  value={displayInputValue}
                  readOnly={isReadOnly}
                  onFocus={() => setIsFocus(true)}
                  onBlur={(e) => {
                    setIsFocus(false)
                    triggerSearchData(
                      getCurrencyValue(e.target.value),
                      'onBlur',
                    )
                    setTimeout(() => setShowDropdown(false), 200)
                  }}
                  onKeyDown={(ev) => {
                    if (handleDropdownKeyDown(ev)) return
                    if (ev.key === 'Enter') {
                      let value = getCurrencyValue(ev.target.value)
                      triggerSearchData(value, 'onEnter')
                      handleEnterAction(ev, uischema, designContext, config)
                      if (searchTrigger === 'onEnter') {
                        ev.preventDefault()
                        ev.stopPropagation()
                      }
                    }
                  }}
                  onChange={(e) => {
                    let stringValue = e.target.value
                    let numberValue = getCurrencyValue(stringValue)
                    updateInputData(numberValue, true)
                    copyData(numberValue)
                    setIsOnChange(true)
                    setKeyword(stringValue)
                  }}
                />
              ) : (
                <Form.Control
                  style={uiStyle}
                  type={format === 'number' ? 'text' : format}
                  id={fieldKey}
                  name={fieldName}
                  minLength={minLength}
                  maxLength={maxLength}
                  placeholder={placeholder}
                  autoComplete="off"
                  readOnly={isReadOnly}
                  value={displayInputValue}
                  aria-autocomplete="list"
                  aria-controls={`input-search-list-${key}`}
                  aria-activedescendant={
                    showDropdown && activeOptionIndex >= 0
                      ? `input-search-option-${key}-${activeOptionIndex + 1}`
                      : undefined
                  }
                  onFocus={() => {
                    setIsFocus(true)
                    if (options.length > 0) {
                      setShowDropdown(true)
                    }
                  }}
                  onBlur={(e) => {
                    setIsFocus(false)
                    triggerSearchData(e.target.value, 'onBlur')
                    setTimeout(() => setShowDropdown(false), 200)
                  }}
                  onChange={(e) => {
                    let value = e.target.value
                    updateInputData(value, true)
                    copyData(value)
                    setIsOnChange(true)
                    setKeyword(value)
                  }}
                  onKeyDown={(ev) => {
                    if (handleDropdownKeyDown(ev)) return
                    if (ev.key === 'Enter') {
                      triggerSearchData(ev.target.value, 'onEnter')
                      handleEnterAction(ev, uischema, designContext, config)
                      ev.preventDefault()
                      ev.stopPropagation()
                    }
                  }}
                />
              )}

              {renderClearButton()}
              <InputAppend
                {...props}
                isReadOnly={isReadOnly}
                uischema={uischema}
              />
            </InputGroup>
            {renderDropdown()}
          </div>
        </Col>
        {suffixText && (
          <Col className="d-flex align-items-center mt-1 pr-0" sm="auto">
            <Form.Label className={`m-0 w-100`} style={{ color: colorByTheme }}>
              {suffixText}
            </Form.Label>
          </Col>
        )}
      </Row>
      <ul className="m-0 mt-2">
        {showRequire && showRequireMessage && requireMessage && (
          <li className="text-danger">{requireMessage}</li>
        )}
        {showWarningMessage && warningMessage && (
          <li className="text-danger">{warningMessage}</li>
        )}
        {errorArray.map((error, index) => (
          <li key={`error-${key}-${index + 1}`} className="text-danger">
            {error}
          </li>
        ))}
      </ul>
    </Form.Group>
  )
}

export default CustomInputSearch
